import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'

import { config } from '@/config'

export interface RouteMapStop {
	order: number
	label: string
	latitude: number
	longitude: number
}

const ROUTE_LINE_COLOR = '#1976d2'

// Encoded polylines are small in practice; refuse absurd inputs rather than
// blocking the chat UI on a malformed payload.
const MAX_POLYLINE_LENGTH = 32768

/** Google encoded polyline, decoded to [lng, lat] pairs (GeoJSON coordinate order). */
/* eslint-disable no-bitwise -- coordinate decoding is inherently bitwise */
function decodePolyline(encoded: string): [number, number][] {
	const points: [number, number][] = []
	if (!encoded || encoded.length > MAX_POLYLINE_LENGTH) return points
	let index = 0
	let lat = 0
	let lng = 0
	while (index < encoded.length) {
		let shift = 0
		let result = 0
		let byte: number
		do {
			byte = encoded.charCodeAt(index++) - 63
			result |= (byte & 0x1f) << shift
			shift += 5
		} while (byte >= 0x20)
		lat += result & 1 ? ~(result >> 1) : result >> 1
		shift = 0
		result = 0
		do {
			byte = encoded.charCodeAt(index++) - 63
			result |= (byte & 0x1f) << shift
			shift += 5
		} while (byte >= 0x20)
		lng += result & 1 ? ~(result >> 1) : result >> 1
		const lngDeg = lng / 1e5
		const latDeg = lat / 1e5
		if (Number.isFinite(lngDeg) && Number.isFinite(latDeg)) {
			points.push([lngDeg, latDeg])
		}
	}
	return points
}
/* eslint-enable no-bitwise -- end of decodePolyline */

// Minimal surface of the Maps JS API this widget uses (no @types/google.maps dep).
interface GoogleLatLngLiteral {
	lat: number
	lng: number
}
interface GoogleMarkerInit {
	position: GoogleLatLngLiteral
	map: GoogleMap
	label?: { text: string; color: string; fontSize: string; fontWeight: string }
	title?: string
	icon?: { url: string; scaledSize?: { width: number; height: number } }
}
interface GoogleMarker {
	addListener: (event: 'click', handler: () => void) => void
	setMap: (map: GoogleMap | null) => void
}
interface GoogleMap {
	fitBounds: (bounds: GoogleLatLngBounds, padding?: number) => void
	setCenter: (center: GoogleLatLngLiteral) => void
	setZoom: (zoom: number) => void
}
interface GoogleLatLngBounds {
	extend: (point: GoogleLatLngLiteral) => GoogleLatLngBounds
	getNorthEast: () => { lat: () => number; lng: () => number }
	getSouthWest: () => { lat: () => number; lng: () => number }
}
interface GooglePolyline {
	setMap: (map: GoogleMap | null) => void
}
interface GoogleInfoWindow {
	setContent: (content: string | Text) => void
	open: (options: { anchor: GoogleMarker; map: GoogleMap }) => void
}
interface GoogleMapsApi {
	Map: new (el: HTMLElement, options: { center: GoogleLatLngLiteral; zoom: number; mapTypeControl: boolean; streetViewControl: boolean; fullscreenControl: boolean }) => GoogleMap
	Marker: new (init: GoogleMarkerInit) => GoogleMarker
	Polyline: new (init: { path: GoogleLatLngLiteral[]; map: GoogleMap; strokeColor: string; strokeWeight: number; strokeOpacity: number }) => GooglePolyline
	LatLngBounds: new () => GoogleLatLngBounds
	InfoWindow: new () => GoogleInfoWindow
}
declare global {
	interface Window {
		google?: { maps?: GoogleMapsApi }
		__routePlanGoogleMaps?: Promise<GoogleMapsApi>
	}
}

const MAPS_SCRIPT_SELECTOR = 'script[data-route-plan-google-maps]'

// With loading=async, `window.google.maps` exists as soon as the bootstrap
// script's 'load' event fires, but the Map/Marker constructors are injected
// asynchronously afterwards (this build has no importLibrary). Resolving at
// 'load' caches a half-initialized API on window.__routePlanGoogleMaps and
// every widget then fails for the whole page session — poll until the
// constructors are usable, with a timeout so a broken bootstrap still fails.
const MAPS_READY_POLL_MS = 50
const MAPS_READY_TIMEOUT_MS = 10_000

function whenMapsReady(): Promise<GoogleMapsApi> {
	return new Promise((resolve, reject) => {
		const deadline = Date.now() + MAPS_READY_TIMEOUT_MS
		const check = () => {
			const maps = window.google?.maps
			if (!maps) {
				reject(new Error('Google Maps failed to initialize'))
			} else if (maps.Map && maps.Marker) {
				resolve(maps)
			} else if (Date.now() >= deadline) {
				reject(new Error('Google Maps constructors unavailable'))
			} else {
				setTimeout(check, MAPS_READY_POLL_MS)
			}
		}
		check()
	})
}

function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
	if (window.google?.maps?.Map && window.google.maps.Marker) {
		return Promise.resolve(window.google.maps)
	}
	if (window.__routePlanGoogleMaps) return window.__routePlanGoogleMaps

	const promise = new Promise<GoogleMapsApi>((resolve, reject) => {
		const existing = document.querySelector<HTMLScriptElement>(MAPS_SCRIPT_SELECTOR)
		const script = existing ?? document.createElement('script')
		if (!existing) {
			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`
			script.async = true
			script.dataset.routePlanGoogleMaps = 'true'
			document.head.appendChild(script)
		}
		// The bootstrap script may already be loaded (e.g. a retry after a failed
		// init) — its 'load' event won't fire again, so when google.maps is
		// already there go straight to the constructor check.
		if (window.google?.maps) {
			whenMapsReady().then(resolve, reject)
		} else {
			script.addEventListener('load', () => {
				whenMapsReady().then(resolve, reject)
			})
		}
		script.addEventListener('error', () => {
			script.remove()
			reject(new Error('Google Maps script failed to load'))
		})
	})
	window.__routePlanGoogleMaps = promise
	// A rejected promise must not stick: the next widget retry reloads the script.
	promise.catch(() => {
		if (window.__routePlanGoogleMaps === promise) {
			window.__routePlanGoogleMaps = undefined
		}
	})
	return promise
}

function numberedPinSvg(order: number): string {
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"><circle cx="13" cy="13" r="11" fill="${ROUTE_LINE_COLOR}" stroke="#ffffff" stroke-width="2"/><text x="13" y="17" text-anchor="middle" font-family="sans-serif" font-size="12" font-weight="600" fill="#ffffff">${order}</text></svg>`
	return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

interface RoutePlanMapProps {
	stops: RouteMapStop[]
	/** Google-encoded overview polyline for the driven route; falls back to straight lines. */
	polyline?: string
	height?: number
}

export function RoutePlanMap({ stops, polyline, height = 220 }: RoutePlanMapProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		const apiKey = config.googleMaps.apiKey
		if (!containerRef.current || stops.length === 0 || !apiKey) return

		let cancelled = false
		let markers: GoogleMarker[] = []
		let line: GooglePolyline | null = null

		loadGoogleMaps(apiKey)
			.then((maps) => {
				if (cancelled || !containerRef.current) return

				const map = new maps.Map(containerRef.current, {
					center: { lat: stops[0].latitude, lng: stops[0].longitude },
					zoom: 11,
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
				})

				const decoded = polyline ? decodePolyline(polyline) : []
				const path = (decoded.length >= 2
					? decoded
					: stops.map((s) => [s.longitude, s.latitude] as [number, number]))
					.map(([lng, lat]) => ({ lat, lng }))
				line = new maps.Polyline({
					path,
					map,
					strokeColor: ROUTE_LINE_COLOR,
					strokeWeight: 3,
					strokeOpacity: 0.8,
				})

				const infoWindow = new maps.InfoWindow()
				markers = stops.map((stop) => {
					const marker = new maps.Marker({
						position: { lat: stop.latitude, lng: stop.longitude },
						map,
						title: stop.label,
						icon: { url: numberedPinSvg(stop.order), scaledSize: { width: 26, height: 26 } },
					})
					marker.addListener('click', () => {
						infoWindow.setContent(document.createTextNode(stop.label))
						infoWindow.open({ anchor: marker, map })
					})
					return marker
				})

				const bounds = new maps.LatLngBounds()
				for (const stop of stops) {
					bounds.extend({ lat: stop.latitude, lng: stop.longitude })
				}
				// fitBounds on a point (one stop, or several sharing an address) zooms to
				// street level with no context — center at a useful zoom instead.
				const ne = bounds.getNorthEast()
				const sw = bounds.getSouthWest()
				if (ne.lat() - sw.lat() < 0.002 && ne.lng() - sw.lng() < 0.002) {
					map.setCenter({ lat: stops[0].latitude, lng: stops[0].longitude })
					map.setZoom(14)
				} else {
					map.fitBounds(bounds, 40)
				}
			})
			.catch(() => {
				if (!cancelled) setFailed(true)
			})

		return () => {
			cancelled = true
			for (const marker of markers) marker.setMap(null)
			line?.setMap(null)
		}
	}, [stops, polyline])

	if (stops.length === 0 || failed || !config.googleMaps.apiKey) return null

	return (
		<Box
			ref={containerRef}
			sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', height, mb: 1 }}
		/>
	)
}
